import { useCallback, useEffect, useRef, useState } from 'react'
import { io, type Socket } from 'socket.io-client'
import type { INoteTranscriptSegment } from '@dataTypes/note.type'
import type { TranscribedSegment } from '@components/pages/Notes/TranscribedAudioPanel.component'
import { transcribeNoteAudio } from '@services/api/note.api'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { getEnv } from '@utils/env'
import { pcmToBase64 } from '@utils/perio-audio.utils'
import {
  lastTranscriptEnd,
  mapTranscriptSegmentsToPanel,
  offsetTranscriptSegments,
} from './notesTranscript.util'
import { logger } from '@utils/safeLogger'
export { mapTranscriptSegmentsToPanel } from './notesTranscript.util'

export type UseNotesLiveTranscribeParams = {
  doctorName?: string
  patientName?: string
  patientId?: string
  clinicId?: string
  onSegmentsChange?: (
    panelSegments: TranscribedSegment[],
    persistable: INoteTranscriptSegment[],
  ) => void
}

export type UseNotesLiveTranscribeResult = {
  segments: INoteTranscriptSegment[]
  panelSegments: TranscribedSegment[]
  isTranscribing: boolean
  isConnecting: boolean
  liveText: string
  startSession: (
    stream: MediaStream,
    options?: { listeningStartedAt?: number },
  ) => Promise<void>
  stopSession: () => void
  /** One diarize pass after stop; appends Doctor/Patient segments onto prior takes. */
  finalizeWithDiarize: (audioBlob: Blob) => Promise<{
    allSegments: INoteTranscriptSegment[]
    takeSegments: INoteTranscriptSegment[]
  }>
  setPaused: (paused: boolean) => void
  reset: () => void
  loadSavedSegments: (
    segments: INoteTranscriptSegment[] | null | undefined,
  ) => void
}

type SessionResources = {
  audio?: AudioContext
  node?: AudioWorkletNode
  socket?: Socket
  sessionId?: string
  clearPending?: () => void
}

const notesBase = (): string => {
  const base = getEnv('REACT_APP_BUSINESS_LAYER_NOTE').replace(/\/$/, '')
  if (!base) throw new Error('The notes service URL is not configured.')
  return base
}

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return `notes_voice_${Date.now()}_${Math.random().toString(36).slice(2)}`
}

export function useNotesLiveTranscribe(
  params: UseNotesLiveTranscribeParams = {},
): UseNotesLiveTranscribeResult {
  const [segments, setSegments] = useState<INoteTranscriptSegment[]>([])
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [liveText, setLiveText] = useState('')
  const segmentsRef = useRef<INoteTranscriptSegment[]>([])
  /** Committed takes only (prior recordings / saved load). */
  const baselineSegmentsRef = useRef<INoteTranscriptSegment[]>([])
  /** Live provisional lines for the current recording session (0-based times). */
  const sessionProvisionalRef = useRef<INoteTranscriptSegment[]>([])
  const generationRef = useRef(0)
  const runningRef = useRef(false)
  const pausedRef = useRef(false)
  const resourcesRef = useRef<SessionResources>({})
  const paramsRef = useRef(params)
  paramsRef.current = params

  const panelSegments = mapTranscriptSegmentsToPanel(segments)

  const publishCommittedView = useCallback(
    (
      baseline: INoteTranscriptSegment[],
      provisional: INoteTranscriptSegment[],
      previewText = '',
    ): void => {
      const offset = lastTranscriptEnd(baseline)
      const next = [
        ...baseline,
        ...offsetTranscriptSegments(provisional, offset),
      ]
      segmentsRef.current = next
      setSegments(next)
      const panel = mapTranscriptSegmentsToPanel(next)
      const trimmed = previewText.trim()
      const withPreview = trimmed
        ? [
            ...panel,
            {
              timestamp: '[···]',
              speaker: 'Speaker',
              text: trimmed,
              highlighted: false,
            },
          ]
        : panel
      paramsRef.current.onSegmentsChange?.(withPreview, next)
    },
    [],
  )

  const publish = useCallback(
    (next: INoteTranscriptSegment[], previewText = ''): void => {
      segmentsRef.current = next
      setSegments(next)
      const panel = mapTranscriptSegmentsToPanel(next)
      const trimmed = previewText.trim()
      const withPreview = trimmed
        ? [
            ...panel,
            {
              timestamp: '[···]',
              speaker: 'Speaker',
              text: trimmed,
              highlighted: false,
            },
          ]
        : panel
      paramsRef.current.onSegmentsChange?.(withPreview, next)
    },
    [],
  )

  const appendSegment = useCallback(
    (segment: INoteTranscriptSegment): void => {
      const text = segment.text.trim()
      if (!text) return
      sessionProvisionalRef.current = [
        ...sessionProvisionalRef.current,
        {
          speaker: segment.speaker || 'Speaker',
          role: segment.role || 'unknown',
          text,
          start: segment.start,
          end: Math.max(segment.start, segment.end),
        },
      ]
      publishCommittedView(
        baselineSegmentsRef.current,
        sessionProvisionalRef.current,
      )
    },
    [publishCommittedView],
  )

  const stopSession = useCallback((): void => {
    generationRef.current += 1
    runningRef.current = false
    pausedRef.current = false
    setIsConnecting(false)
    setIsTranscribing(false)
    setLiveText('')
    const old = resourcesRef.current
    resourcesRef.current = {}
    if (old.socket && old.sessionId) {
      old.socket.emit('notes_voice:stop', { sessionId: old.sessionId })
    }
    old.socket?.removeAllListeners()
    old.socket?.disconnect()
    old.node?.disconnect()
    void old.audio?.close()
  }, [])

  const setPaused = useCallback(
    (paused: boolean): void => {
      if (paused === pausedRef.current) return
      if (paused) {
        resourcesRef.current.clearPending?.()
        setLiveText('')
        publishCommittedView(
          baselineSegmentsRef.current,
          sessionProvisionalRef.current,
        )
      }
      pausedRef.current = paused
      const { socket, sessionId } = resourcesRef.current
      if (socket && sessionId) {
        socket.emit('notes_voice:pause', { sessionId, paused })
      }
    },
    [publishCommittedView],
  )

  const finalizeWithDiarize = useCallback(
    async (
      audioBlob: Blob,
    ): Promise<{
      allSegments: INoteTranscriptSegment[]
      takeSegments: INoteTranscriptSegment[]
    }> => {
      const empty = {
        allSegments: segmentsRef.current,
        takeSegments: [] as INoteTranscriptSegment[],
      }
      if (audioBlob.size === 0) return empty
      setIsTranscribing(true)
      setLiveText('')
      const baseline = baselineSegmentsRef.current.map((segment) => ({
        ...segment,
      }))
      const provisionalTake = sessionProvisionalRef.current.map((segment) => ({
        ...segment,
      }))
      try {
        const { doctorName, patientName, patientId } = paramsRef.current
        const response = await transcribeNoteAudio({
          audioBlob,
          doctorName,
          patientName,
          patientId,
        })
        if (
          response.statusCode === 200 &&
          Array.isArray(response.data?.segments) &&
          response.data.segments.length > 0
        ) {
          const takeSegments = response.data.segments.map((segment) => ({
            ...segment,
          }))
          const offset = lastTranscriptEnd(baseline)
          const next = [
            ...baseline,
            ...offsetTranscriptSegments(takeSegments, offset),
          ]
          sessionProvisionalRef.current = []
          baselineSegmentsRef.current = next.map((segment) => ({ ...segment }))
          publish(next)
          return { allSegments: next, takeSegments }
        }
        // Diarize failed/empty: keep provisional (0-based for this take) so times stay usable.
        const takeSegments = provisionalTake
        const offset = lastTranscriptEnd(baseline)
        const next =
          takeSegments.length > 0
            ? [...baseline, ...offsetTranscriptSegments(takeSegments, offset)]
            : baseline
        sessionProvisionalRef.current = []
        baselineSegmentsRef.current = next.map((segment) => ({ ...segment }))
        publish(next)
        return { allSegments: next, takeSegments }
      } catch (error) {
        logger.error('Notes diarize remap on stop failed:', error)
        const takeSegments = provisionalTake
        const offset = lastTranscriptEnd(baseline)
        const next =
          takeSegments.length > 0
            ? [...baseline, ...offsetTranscriptSegments(takeSegments, offset)]
            : baseline
        sessionProvisionalRef.current = []
        baselineSegmentsRef.current = next.map((segment) => ({ ...segment }))
        publish(next)
        return { allSegments: next, takeSegments }
      } finally {
        setIsTranscribing(false)
      }
    },
    [publish],
  )

  const startSession = useCallback(
    async (stream: MediaStream): Promise<void> => {
      if (runningRef.current) return
      const clinicId = paramsRef.current.clinicId?.trim()
      if (!clinicId) {
        throw new Error('Select a clinic before using live transcription.')
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(
          'Microphone capture is unavailable. Use a supported browser over HTTPS (or localhost).',
        )
      }

      runningRef.current = true
      pausedRef.current = false
      setIsConnecting(true)
      if (sessionProvisionalRef.current.length === 0) {
        baselineSegmentsRef.current = segmentsRef.current.map((segment) => ({
          ...segment,
        }))
      }
      sessionProvisionalRef.current = []
      publish(baselineSegmentsRef.current)
      const gen = ++generationRef.current
      const valid = (): boolean =>
        gen === generationRef.current && runningRef.current
      let resolveConnected: (() => void) | null = null
      let rejectConnected: ((error: unknown) => void) | null = null
      const connected = new Promise<void>((resolve, reject) => {
        resolveConnected = resolve
        rejectConnected = reject
      })
      const fail = (error: unknown): void => {
        if (!valid()) return
        logger.error('Notes live voice session failed:', error)
        rejectConnected?.(error)
        setIsConnecting(false)
        stopSession()
      }

      setIsTranscribing(true)
      setLiveText('')
      const sessionId = createSessionId()

      let proxyReady = false
      let openTurns = 0

      try {
        const token = getAccessToken()
        const dbName = localStorage.getItem('db_name')
        if (!token) {
          throw new Error('Sign in again before using live transcription.')
        }
        const socket = io(notesBase(), {
          path: '/api/v1/notes/socket.io/',
          transports: ['websocket'],
          reconnection: false,
          autoConnect: false,
          auth: { token, dbName: dbName ?? undefined },
        })
        resourcesRef.current.socket = socket
        resourcesRef.current.sessionId = sessionId
        resourcesRef.current.clearPending = () => {
          openTurns = 0
          socket.emit('notes_voice:clear', { sessionId })
        }
        socket.on('connect_error', (error) => fail(error))
        socket.on('disconnect', (reason) => {
          if (runningRef.current && valid()) {
            fail(new Error(`Voice connection closed: ${reason}`))
          }
        })
        socket.on(
          'notes_voice:partial',
          (payload: { sessionId: string; text: string }) => {
            if (!valid() || payload.sessionId !== sessionId) return
            const preview = payload.text.trim()
            setLiveText(preview)
            publishCommittedView(
              baselineSegmentsRef.current,
              sessionProvisionalRef.current,
              preview,
            )
          },
        )
        socket.on(
          'notes_voice:final',
          (payload: { sessionId: string; text: string; failed?: boolean }) => {
            if (!valid() || payload.sessionId !== sessionId) return
            openTurns = Math.max(0, openTurns - 1)
            setLiveText('')
            publishCommittedView(
              baselineSegmentsRef.current,
              sessionProvisionalRef.current,
            )
          },
        )
        socket.on(
          'notes_voice:segment',
          (payload: INoteTranscriptSegment & { sessionId: string }) => {
            if (!valid() || payload.sessionId !== sessionId) return
            appendSegment(payload)
          },
        )
        socket.on(
          'notes_voice:error',
          (payload: { sessionId?: string; message: string }) => {
            if (!valid()) return
            if (payload.sessionId && payload.sessionId !== sessionId) return
            fail(new Error(payload.message))
          },
        )
        socket.on('notes_voice:ready', (payload: { sessionId: string }) => {
          if (!valid() || payload.sessionId !== sessionId) return
          proxyReady = true
          setIsConnecting(false)
          resolveConnected?.()
        })

        const audio = new AudioContext({ sampleRate: 24000 })
        resourcesRef.current.audio = audio
        await audio.audioWorklet.addModule('/perio-stream-capture.js')
        if (!valid()) return
        if (audio.sampleRate !== 24000) {
          throw new Error(
            'This browser cannot capture at the required sample rate.',
          )
        }
        const node = new AudioWorkletNode(audio, 'perio-stream-capture')
        resourcesRef.current.node = node
        node.onprocessorerror = (): void =>
          fail(new Error('Audio capture failed. Restart the microphone.'))
        const mute = audio.createGain()
        mute.gain.value = 0
        node.port.onmessage = (
          event: MessageEvent<{
            type: string
            audio?: Float32Array
            error?: string
          }>,
        ): void => {
          if (!valid() || pausedRef.current || !proxyReady) return
          try {
            if (event.data.type === 'error') throw new Error(event.data.error)
            if (event.data.type === 'start') {
              if (openTurns >= 8) {
                throw new Error(
                  'Too many unfinished phrases. Pause briefly, then continue.',
                )
              }
              openTurns += 1
              setLiveText('')
            } else if (event.data.type === 'audio' && event.data.audio) {
              socket.emit('notes_voice:audio.append', {
                sessionId,
                audio: pcmToBase64(event.data.audio),
              })
            } else if (event.data.type === 'commit') {
              socket.emit('notes_voice:audio.commit', { sessionId })
            }
          } catch (error) {
            fail(error)
          }
        }
        audio.createMediaStreamSource(stream).connect(node)
        node.connect(mute).connect(audio.destination)
        await audio.resume()
        if (!valid()) return
        socket.on('connect', () => {
          if (!valid()) return
          socket.emit('notes_voice:start', {
            sessionId,
            patient_id: paramsRef.current.patientId?.trim() || '',
            clinic_id: clinicId,
          })
        })
        socket.connect()
        await connected
      } catch (error) {
        fail(error)
        throw error
      }
    },
    [appendSegment, publish, publishCommittedView, stopSession],
  )

  const reset = useCallback((): void => {
    stopSession()
    baselineSegmentsRef.current = []
    sessionProvisionalRef.current = []
    publish([])
  }, [publish, stopSession])

  const loadSavedSegments = useCallback(
    (saved: INoteTranscriptSegment[] | null | undefined): void => {
      const next = Array.isArray(saved) ? saved : []
      baselineSegmentsRef.current = next.map((segment) => ({ ...segment }))
      sessionProvisionalRef.current = []
      publish(next)
    },
    [publish],
  )

  useEffect(() => {
    return () => {
      stopSession()
    }
  }, [stopSession])

  return {
    segments,
    panelSegments,
    isTranscribing,
    isConnecting,
    liveText,
    startSession,
    stopSession,
    finalizeWithDiarize,
    setPaused,
    reset,
    loadSavedSegments,
  }
}
